import type {
  CalendarReadiness,
  CalendarReadinessState,
  Conversation,
  ConversationNote,
  ConversationThread,
  FollowUp,
  MediaAsset,
  Message,
  MessagingAvailability,
  MessagingAvailabilityValue,
  MessengerRuntimeFeatures,
  Recommendation,
  SuggestionFeedbackResponse,
} from '@crm/messenger';
import { MESSAGE_CONTENT_UNAVAILABLE } from '@crm/messenger';

export type JsonRecord = Record<string, unknown>;

export function asRecord(value: unknown): JsonRecord | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as JsonRecord)
    : null;
}

export function asRecordArray(value: unknown): JsonRecord[] {
  return Array.isArray(value)
    ? value.filter((item): item is JsonRecord => asRecord(item) !== null)
    : [];
}

export function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

export function textValue(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }
  const record = asRecord(value);
  if (record) {
    return (
      textValue(record['displayName']) ||
      textValue(record['name']) ||
      textValue(record['value'])
    );
  }
  return '';
}

function phoneValue(value: unknown): string {
  const text = textValue(value);
  const match = /\+?\d[\d\s().-]{6,}\d/.exec(text);
  return match?.[0]?.trim() ?? '';
}

function phoneFrom(value: unknown): string {
  const record = asRecord(value);
  if (!record) return '';
  const keys = [
    'phoneNumber',
    'mobilePhone',
    'mobileNumber',
    'phone',
    'telephone',
    'contactValue',
    'address',
    'value',
  ];
  for (const key of keys) {
    const phone = phoneValue(record[key]);
    if (phone) return phone;
  }
  const contacts = [
    ...asRecordArray(record['contacts']),
    ...asRecordArray(record['contactPoints']),
    ...asRecordArray(record['channels']),
  ];
  for (const contact of contacts) {
    const phone = phoneFrom(contact);
    if (phone) return phone;
  }
  return '';
}

export function collectionItems(response: unknown): JsonRecord[] {
  if (Array.isArray(response)) return asRecordArray(response);
  const record = asRecord(response);
  if (record) {
    if (Array.isArray(record['items'])) return asRecordArray(record['items']);
    const data = asRecord(record['data']);
    if (data && Array.isArray(data['items'])) return asRecordArray(data['items']);
  }
  return [];
}

export function firstItem(response: unknown): JsonRecord | null {
  const items = collectionItems(response);
  if (items.length) return items[0] ?? null;
  const record = asRecord(response);
  return asRecord(record?.['item']) ?? record;
}

function customerPhone(item: JsonRecord): string {
  const direct = [
    item['customerPhoneNumber'],
    asRecord(item['customer'])?.['phoneNumber'],
    asRecord(item['customer'])?.['mobilePhone'],
    asRecord(item['contact'])?.['phoneNumber'],
    asRecord(item['borrower'])?.['phoneNumber'],
  ]
    .map(phoneValue)
    .find(Boolean);
  if (direct) return direct;

  const participants = [
    ...asRecordArray(item['participants']),
    ...asRecordArray(item['conversationParticipants']),
  ];
  const customer = participants.find((participant) =>
    /customer|borrower|consumer|lead|contact/i.test(
      [
        participant['role'],
        participant['type'],
        participant['participantType'],
        participant['contactChannel'],
      ]
        .map(textValue)
        .join(' '),
    ),
  );
  const participantPhone =
    phoneFrom(customer) ||
    participants
      .filter(
        (participant) =>
          !/agent|assistant|loan officer|originator|employee/i.test(
            [
              participant['role'],
              participant['type'],
              participant['participantType'],
            ]
              .map(textValue)
              .join(' '),
          ),
      )
      .map(phoneFrom)
      .find(Boolean);
  if (participantPhone) return participantPhone;

  const messages = messageRecords(item);
  for (const message of messages) {
    const inbound =
      message['direction'] === 'inbound' || message['senderType'] === 'customer';
    const candidates = inbound
      ? [
          message['fromPhoneNumber'],
          message['senderPhoneNumber'],
          message['from'],
          message['source'],
        ]
      : [
          message['toPhoneNumber'],
          message['recipientPhoneNumber'],
          message['to'],
          message['destination'],
        ];
    const phone = candidates.map(phoneValue).find(Boolean);
    if (phone) return phone;
  }
  return '';
}

function senderAttribution(item: JsonRecord, outbound: boolean): {
  key: Message['senderPersona'];
  label: string;
} {
  if (!outbound) return { key: 'customer', label: 'Customer' };
  const metadata = asRecord(item['metadata']);
  const origin = [
    item['senderOrigin'],
    item['origin'],
    item['source'],
    item['sentByType'],
    metadata?.['senderOrigin'],
    metadata?.['source'],
  ]
    .map(textValue)
    .join(' ')
    .toLowerCase();
  const persona = [
    item['senderPersona'],
    item['persona'],
    asRecord(item['sender'])?.['persona'],
    asRecord(item['sentBy'])?.['persona'],
    metadata?.['senderPersona'],
  ]
    .map(textValue)
    .join(' ')
    .toLowerCase();
  const content = textValue(item['content']).toLowerCase();
  const assistantCopy = [
    /\b(ai|virtual|loandepot) assistant\b/,
    /on behalf of your loan officer/,
    /\bpassed this to your loan officer\b/,
  ].some((pattern) => pattern.test(content));
  const nurture =
    /nurture|automat|assistant|bot/.test(`${origin} ${persona}`) ||
    item['nurture'] === true ||
    item['sentByNurture'] === true ||
    assistantCopy;
  const loanOfficer = /loan.?officer|originator|\blo\b/.test(persona);
  if (nurture) {
    return loanOfficer
      ? { key: 'nurture-loan-officer', label: 'Nurture · Loan officer' }
      : { key: 'nurture-assistant', label: 'Nurture · Assistant' };
  }
  return { key: 'loan-officer', label: 'Loan officer' };
}

function customerName(item: JsonRecord): string {
  const customer = asRecord(item['customer']);
  return (
    textValue(customer?.['displayName']) ||
    textValue(item['customerDisplayName']) ||
    [textValue(customer?.['firstName']), textValue(customer?.['lastName'])]
      .filter(Boolean)
      .join(' ') ||
    'Customer details pending'
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function relativeTime(value: unknown): string {
  const timestamp = Date.parse(typeof value === 'string' ? value : '');
  if (!Number.isFinite(timestamp)) return 'No activity';
  const minutes = Math.max(0, Math.round((Date.now() - timestamp) / 60000));
  if (minutes < 60) return `${minutes || 1} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  return `${Math.round(hours / 24)} days ago`;
}

function conversationPreview(item: JsonRecord): string {
  const lastMessage = asRecord(item['lastMessage']);
  const content = textValue(lastMessage?.['content']).replace(/\s+/g, ' ');
  if (!content) {
    return Number(item['unreadCount'] || 0) > 0
      ? 'Customer replied'
      : 'Conversation updated';
  }
  const limit = 96;
  return content.length > limit
    ? `${content.slice(0, limit - 1).trimEnd()}…`
    : content;
}

function conversationContext(item: JsonRecord): string {
  const snapshot = asRecord(asRecord(item['contextReadback'])?.['safeSnapshot']);
  return (
    textValue(snapshot?.['loanStage']) ||
    textValue(snapshot?.['leadStatus']) ||
    textValue(asRecord(item['recordRef'])?.['type']) ||
    textValue(item['channelId']).toUpperCase() ||
    'Conversation'
  );
}

function messageRecords(item: JsonRecord): JsonRecord[] {
  const messages = asRecordArray(item['messages']);
  if (messages.length) return messages;
  const last = asRecord(item['lastMessage']);
  return last ? [last] : [];
}

function nurtureStatus(item: JsonRecord): string {
  return (
    textValue(asRecord(item['nurtureState'])?.['status']) ||
    textValue(asRecord(item['nurture'])?.['status']) ||
    textValue(item['nurtureStatus']) ||
    'unknown'
  );
}

export function conversationAliasIds(item: JsonRecord): string[] {
  const aliases = [
    textValue(item['conversationId']),
    textValue(item['id']),
    textValue(asRecord(item['sourceAliases'])?.['mcsConversationGuidRef']),
  ].filter((alias): alias is string => Boolean(alias));
  const expanded = aliases.flatMap((alias) => {
    const stripped = alias.startsWith('mcs-') ? alias.slice(4) : alias;
    return /^[0-9a-f-]{36}$/i.test(stripped)
      ? [alias, stripped, `mcs-${stripped}`]
      : [alias];
  });
  return [...new Set(expanded)];
}

export function normalizeConversation(item: JsonRecord): Conversation {
  const name = customerName(item);
  const lastActivity =
    item['lastMessageAt'] ||
    item['lastActivityAt'] ||
    item['updatedAt'] ||
    item['createdAt'];
  const snapshot = asRecord(asRecord(item['contextReadback'])?.['safeSnapshot']);
  const lastMessage = asRecord(item['lastMessage']);
  return {
    id: textValue(item['conversationId']) || textValue(item['id']),
    customerName: name,
    initials: initials(name),
    channel: (textValue(item['channelId']) || 'sms').toUpperCase(),
    context: conversationContext(item),
    customerPhone: customerPhone(item),
    customerEmail:
      textValue(asRecord(item['customer'])?.['email']) ||
      textValue(item['customerEmail']),
    leadRef: textValue(snapshot?.['leadRef']) || textValue(item['leadRef']),
    loanRef: textValue(snapshot?.['loanRef']) || textValue(item['loanRef']),
    preview: conversationPreview(item),
    lastActivityAt: textValue(lastActivity),
    timeAgo: relativeTime(lastActivity),
    unreadCount: Number(item['unreadCount'] || 0),
    pinned: item['pinned'] === true,
    flagged: item['flagged'] === true,
    archived:
      item['archiveState'] === 'archived' || item['status'] === 'closed',
    status: textValue(item['status']) || 'open',
    lastDeliveryFailed:
      lastMessage?.['status'] === 'failed' ||
      Boolean(lastMessage?.['deliveryFailure']),
    nurtureStatus: nurtureStatus(item),
    aliases: conversationAliasIds(item),
  };
}

export function normalizeMessage(
  item: JsonRecord,
  conversationName = '',
): Message {
  const outbound =
    item['direction'] === 'outbound' || item['senderType'] === 'agent';
  const name = outbound ? 'You' : conversationName;
  const createdAt = textValue(item['createdAt']) || new Date().toISOString();
  const attribution = senderAttribution(item, outbound);
  const deliveryFailure = item['deliveryFailure'];
  return {
    id:
      textValue(item['messageId']) ||
      textValue(item['id']) ||
      `msg-${crypto.randomUUID()}`,
    clientMessageId: textValue(item['clientMessageId']) || undefined,
    direction: outbound ? 'outbound' : 'inbound',
    senderPersona: attribution.key,
    senderLabel: attribution.label,
    who: name,
    initials: initials(name),
    channel: textValue(item['channelId']).toUpperCase() || 'SMS',
    sentAt: createdAt,
    timeLabel: new Date(createdAt).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }),
    status: textValue(item['status']) || (outbound ? 'Sent' : 'Received'),
    text: textValue(item['content']) || MESSAGE_CONTENT_UNAVAILABLE,
    attachments: Array.isArray(item['attachments']) ? item['attachments'] : [],
    acknowledged: item['acknowledged'] === true,
    failed:
      item['status'] === 'failed' || (deliveryFailure !== undefined && deliveryFailure !== null && deliveryFailure !== false),
    isLastRead: false,
    ...(textValue(item['sendIntentId'])
      ? { sendIntentId: textValue(item['sendIntentId']) }
      : {}),
    ...(textValue(item['providerMessageGuid']) || textValue(item['providerMessageRef'])
      ? {
          providerMessageId:
            textValue(item['providerMessageGuid']) ||
            textValue(item['providerMessageRef']),
        }
      : {}),
    ...(item['sendVCard'] === true ||
    asRecord(item['metadata'])?.['sendVCard'] === true
      ? { contactCard: true }
      : {}),
  };
}

export function normalizeRuntimeFeatures(
  response: unknown,
): MessengerRuntimeFeatures {
  const record = asRecord(response);
  const payload = asRecord(record?.['data']) ?? record;
  const flags = asRecord(payload?.['featureFlags']) ?? payload ?? {};
  const features: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(flags)) {
    if (typeof value === 'boolean') features[key] = value;
  }
  return features;
}

export function normalizeRecommendations(
  response: unknown,
): Recommendation[] {
  const record = asRecord(response);
  const payload = asRecord(record?.['data']) ?? record;
  const batchId =
    textValue(payload?.['suggestionBatchId']) || textValue(payload?.['batchId']);
  const batchVersion =
    (payload?.['suggestionBatchVersion'] as string | number | undefined) ??
    (payload?.['batchVersion'] as string | number | undefined);
  const items = Array.isArray(response)
    ? asRecordArray(response)
    : asRecordArray(payload?.['responses']).length
      ? asRecordArray(payload?.['responses'])
      : asRecordArray(payload?.['suggestions']).length
        ? asRecordArray(payload?.['suggestions'])
        : asRecordArray(payload?.['items']);
  return items
    .map((item, index) => ({
      id:
        textValue(item['suggestionId']) ||
        textValue(item['id']) ||
        `suggestion-${index}`,
      content:
        textValue(item['content']) ||
        textValue(item['text']) ||
        textValue(item['body']),
      kind: textValue(item['kind']) || textValue(item['type']) || 'reply',
      suggestionBatchId:
        textValue(item['suggestionBatchId']) || batchId || undefined,
      suggestionBatchVersion:
        (item['suggestionBatchVersion'] as string | number | undefined) ??
        batchVersion,
      suggestionId: textValue(item['suggestionId']) || undefined,
      triggerMessageId:
        textValue(item['triggerMessageId']) ||
        textValue(payload?.['triggerMessageId']) ||
        undefined,
      modelVersion:
        textValue(item['modelVersion']) ||
        textValue(payload?.['modelVersion']) ||
        undefined,
      promptVersion:
        textValue(item['promptVersion']) ||
        textValue(payload?.['promptVersion']) ||
        undefined,
    }))
    .filter((item) => item.content);
}

export function normalizeSuggestionFeedbackResponse(
  response: unknown,
  fallbackCorrelationId = '',
): SuggestionFeedbackResponse {
  const record = asRecord(response);
  const payload = asRecord(record?.['data']) ?? record ?? {};
  const persisted =
    typeof payload['persisted'] === 'boolean'
      ? payload['persisted']
      : undefined;
  const accepted =
    payload['accepted'] === true && persisted !== false;
  const correlationId =
    textValue(payload['correlationId']) || fallbackCorrelationId;
  const feedbackId = textValue(payload['feedbackId']) || undefined;
  return {
    accepted,
    correlationId,
    ...(feedbackId ? { feedbackId } : {}),
    ...(persisted !== undefined ? { persisted } : {}),
  };
}

export function normalizeFollowUp(item: JsonRecord): FollowUp {
  const source = asRecord(item['sourceContext']);
  return {
    id: textValue(item['followUpRecordId']) || textValue(item['id']),
    type: textValue(item['type']) || 'reminder',
    startsAt: textValue(item['startsAt']),
    endsAt: textValue(item['endsAt']),
    status:
      textValue(item['status']) || textValue(source?.['status']) || 'pending',
    details:
      textValue(item['agenda']) ||
      textValue(item['note']) ||
      textValue(item['details']) ||
      'Customer follow-up',
    channel: textValue(item['channel']),
  };
}

export function normalizeCalendarReadiness(
  response: unknown,
): CalendarReadiness {
  const record = asRecord(response);
  const graph = asRecord(record?.['graph']) ?? record ?? {};
  const rawStatus = textValue(graph['status']).toLowerCase();
  const status: CalendarReadinessState =
    rawStatus === 'configured'
      ? 'configured'
      : rawStatus === 'denied'
        ? 'denied'
        : 'unavailable';
  const configured =
    graph['configured'] === true ||
    graph['readyForLive'] === true ||
    status === 'configured';
  return {
    status,
    configured,
    readyForLive: graph['readyForLive'] === true,
    canMutateLive: graph['canMutateLive'] === true,
    mutationAllowed:
      graph['mutationAllowed'] === true || graph['canMutateLive'] === true,
    missingConfigKeys: asStringArray(graph['missingConfigKeys']),
    configuredConfigKeys: asStringArray(graph['configuredConfigKeys']),
    reasonCodes: asStringArray(graph['reasonCodes']),
  };
}

export function normalizeMessagingAvailability(
  response: unknown,
): MessagingAvailability {
  const record = asRecord(response);
  const payload = asRecord(record?.['data']) ?? record ?? {};
  const rawValue = textValue(payload['value']).toLowerCase();
  const value: MessagingAvailabilityValue =
    rawValue === 'away' ? 'away' : 'available';
  const rawVersion = payload['version'];
  const version =
    typeof rawVersion === 'number' &&
    Number.isSafeInteger(rawVersion) &&
    rawVersion >= 0
      ? rawVersion
      : 0;
  const sourceState = asRecord(payload['sourceState']);
  const freshUntil =
    textValue(sourceState?.['freshUntil']) || textValue(payload['freshUntil']);
  return {
    value,
    version,
    manualOverrideActive: payload['manualOverrideActive'] === true,
    ...(textValue(payload['effectiveSource'])
      ? { effectiveSource: textValue(payload['effectiveSource']) }
      : {}),
    ...(freshUntil ? { freshUntil } : {}),
  };
}

export function normalizeNote(item: JsonRecord): ConversationNote {
  return {
    id: textValue(item['noteId']) || textValue(item['id']),
    body:
      textValue(item['body']) ||
      textValue(item['content']) ||
      textValue(item['note']) ||
      textValue(item['text']),
    createdAt: textValue(item['createdAt']),
    archived:
      item['archived'] === true ||
      item['status'] === 'archived' ||
      item['archiveState'] === 'archived',
    author:
      textValue(item['author']) ||
      textValue(item['authorName']) ||
      textValue(item['createdBy']) ||
      textValue(item['createdByName']) ||
      textValue(item['userName']) ||
      textValue(asRecord(item['user'])?.['displayName']),
    generated:
      item['generated'] === true ||
      item['aiGenerated'] === true ||
      item['isGenerated'] === true ||
      textValue(item['source']) === 'ai' ||
      textValue(item['kind']) === 'generated',
    mine:
      item['mine'] === true ||
      item['isMine'] === true ||
      item['ownNote'] === true ||
      item['createdByCurrentUser'] === true,
  };
}

export function mediaVersionRecord(asset: JsonRecord): JsonRecord | null {
  const current = asRecord(asset['currentVersion']);
  if (current) return current;
  const versions = asRecordArray(asset['versions']);
  const currentId = textValue(asset['currentVersionId']);
  return (
    versions.find(
      (version) => textValue(version['mediaVersionId']) === currentId,
    ) ??
    versions[0] ??
    null
  );
}

export function isActiveGifAsset(asset: JsonRecord): boolean {
  const version = mediaVersionRecord(asset);
  const contentType =
    textValue(asRecord(version?.['storage'])?.['contentType']) ||
    textValue(version?.['contentType']);
  return asset['lifecycleState'] === 'active' && contentType === 'image/gif';
}

export function normalizeMediaAsset(
  asset: JsonRecord,
  previewUrl: string,
): MediaAsset {
  const source = asRecord(asset['source']);
  return {
    id: textValue(asset['id']) || textValue(asset['assetId']),
    name: textValue(asset['name']) || textValue(asset['displayName']),
    alt: textValue(asset['description']) || textValue(asset['displayName']),
    url: textValue(asset['url']) || previewUrl || textValue(source?.['safeUrlPreview']),
    thumbnailUrl:
      textValue(asset['thumbnailUrl']) ||
      previewUrl ||
      textValue(source?.['safeUrlPreview']),
    category:
      textValue(asset['category']) ||
      asStringArray(asset['tags'])[0] ||
      'Library',
  };
}

export function normalizeThread(item: JsonRecord): ConversationThread {
  const conversation = normalizeConversation(item);
  const messages = messageRecords(item).map((message) =>
    normalizeMessage(message, conversation.customerName),
  );
  let lastReadIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (
      message &&
      (message.acknowledged ||
        ['read', 'delivered'].includes(message.status.toLowerCase()))
    ) {
      lastReadIndex = i;
      break;
    }
  }
  if (lastReadIndex < 0 && messages.length) lastReadIndex = messages.length - 1;
  return {
    conversation,
    messages: messages.map((message, index) => ({
      ...message,
      isLastRead: index === lastReadIndex,
    })),
  };
}

const HUB_CONVERSATION_FIELDS = [
  'ConversationGuid',
  'conversationGuid',
  'ConversationId',
  'conversationId',
  'legacyConversationGuid',
] as const;

function hubField(payload: JsonRecord | null, names: readonly string[]): unknown {
  for (const name of names) {
    const value = payload?.[name];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

function hubText(payload: JsonRecord | null, names: readonly string[]): string {
  const value = hubField(payload, names);
  return typeof value === 'string' ? value.trim() : '';
}

export function hubConversationId(payload: unknown): string {
  return hubText(asRecord(payload), HUB_CONVERSATION_FIELDS);
}

export type ParticipantDirection = 'inbound' | 'outbound';

/** Maps each conversation participant guid to who it is (agent = outbound). */
export function participantDirectionsFrom(payload: unknown): Map<string, ParticipantDirection> {
  const directions = new Map<string, ParticipantDirection>();
  const conversations = Array.isArray(payload) ? payload.flat() : [];
  for (const conversation of conversations) {
    const participants = hubField(asRecord(conversation), ['participants', 'Participants']);
    if (!Array.isArray(participants)) continue;
    for (const raw of participants) {
      const participant = asRecord(raw);
      const guid = hubText(participant, ['conversationParticipantGuid', 'ConversationParticipantGuid']);
      if (!guid) continue;
      const channel = hubText(participant, ['contactChannel', 'ContactChannel']);
      directions.set(guid.toLowerCase(), channel === 'MelloConversation' ? 'outbound' : 'inbound');
    }
  }
  return directions;
}

function hubAttachments(payload: JsonRecord | null): unknown[] {
  const objects = hubField(payload, ['attachments', 'Attachments']);
  if (Array.isArray(objects)) {
    return objects
      .map((entry) => {
        const record = asRecord(entry);
        const url = hubText(record, ['url', 'Url', 'href', 'Href']);
        return url
          ? {
              name: hubText(record, ['name', 'Name', 'fileName', 'FileName']) || 'Attachment',
              url,
              contentType: hubText(record, ['contentType', 'ContentType', 'type', 'Type']),
            }
          : null;
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  }
  const urls = hubField(payload, ['attachmentUrls', 'AttachmentUrls']);
  return Array.isArray(urls)
    ? urls.filter((url): url is string => typeof url === 'string').map((url) => ({ name: 'Attachment', url }))
    : [];
}

function hubDeliveryStatus(raw: string, acknowledged: boolean): string {
  const status = raw.toLowerCase().replace(/[^a-z]/g, '');
  if (['failed', 'undelivered', 'rejected', 'blocked', 'blockedbyrecipientpolicy', 'error'].includes(status)) {
    return 'Failed';
  }
  if (acknowledged) return 'Read';
  if (status === 'delivered') return 'Delivered';
  if (status === 'sent') return 'Sent';
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Received';
}

/**
 * Maps an MCS hub `broadcastMessage` payload. Broadcasts carry no direction,
 * so who sent it comes from the participant directory built at connect time.
 */
export function normalizeMcsHubMessage(
  payload: unknown,
  directions: ReadonlyMap<string, ParticipantDirection>,
): { conversationId: string; message: Message } | null {
  const record = asRecord(payload);
  const conversationId = hubConversationId(record);
  if (!record || !conversationId) return null;
  const guid = hubText(record, [
    'ConversationMessageGuid',
    'conversationMessageGuid',
    'MessageGuid',
    'messageGuid',
  ]).replace(/[{}]/g, '').toLowerCase();
  const providerId = guid || hubText(record, ['MessageId', 'messageId', 'ProviderMessageId', 'providerMessageId']);
  const participant = hubText(record, ['ConversationParticipantGuid', 'conversationParticipantGuid']).toLowerCase();
  const explicit = hubText(record, ['Direction', 'direction']).toLowerCase();
  const outbound =
    explicit === 'outbound' ||
    explicit === 'sent' ||
    (explicit !== 'inbound' && directions.get(participant) === 'outbound');
  const sentAt = hubText(record, ['DateSent', 'dateSent', 'CreatedOn', 'createdOn']) || new Date().toISOString();
  const acknowledged = hubField(record, ['IsAcknowledged', 'isAcknowledged']) === true;
  const text = hubText(record, ['Text', 'text', 'Message', 'message', 'Body', 'body', 'Content', 'content', 'MessageBody', 'messageBody']);
  const attachments = hubAttachments(record);
  return {
    conversationId,
    message: {
      id: providerId || `live-${crypto.randomUUID()}`,
      direction: outbound ? 'outbound' : 'inbound',
      senderPersona: outbound ? 'loan-officer' : 'customer',
      senderLabel: outbound ? 'Loan officer' : 'Customer',
      who: outbound ? 'You' : '',
      initials: outbound ? 'YO' : '',
      channel: attachments.length ? 'MMS' : 'SMS',
      sentAt,
      timeLabel: new Date(sentAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      status: hubDeliveryStatus(hubText(record, ['DeliveryStatus', 'deliveryStatus']), acknowledged),
      text: text || (attachments.length ? '' : MESSAGE_CONTENT_UNAVAILABLE),
      attachments,
      acknowledged,
      failed: hubDeliveryStatus(hubText(record, ['DeliveryStatus', 'deliveryStatus']), false) === 'Failed',
      isLastRead: false,
      ...(providerId ? { providerMessageId: providerId } : {}),
    },
  };
}
